import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Barbell } from '@phosphor-icons/react';
import { useAuth, formatApiErrorDetail } from '@/contexts/AuthContext';
import { REGISTER } from '@/constants/testIds';

export default function Register() {
    const { register } = useAuth();
    const navigate = useNavigate();
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);

    async function onSubmit(e) {
        e.preventDefault();
        setError('');
        if (password.length < 8) {
            setError('Password must be at least 8 characters.');
            return;
        }
        if (password !== confirm) {
            setError('Passwords do not match.');
            return;
        }
        setSubmitting(true);
        try {
            await register(email, password, name);
            navigate('/account', { replace: true });
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <main className="mx-auto max-w-md px-5 sm:px-8 pt-16 pb-32">
            <div className="glass-card rounded-3xl p-7 sm:p-9 animate-fade-up">
                <div className="flex items-center gap-3 mb-6">
                    <span className="grid place-items-center w-10 h-10 rounded-2xl bg-foreground/5 border border-foreground/10">
                        <Barbell size={20} weight="bold" />
                    </span>
                    <div>
                        <p className="uppercase-mono text-muted-foreground">
                            Start training
                        </p>
                        <h1 className="font-display font-extrabold tracking-tighter text-2xl">
                            Create your account
                        </h1>
                    </div>
                </div>

                <form onSubmit={onSubmit} className="space-y-4">
                    <Field
                        label="Name"
                        type="text"
                        testId={REGISTER.nameInput}
                        value={name}
                        onChange={setName}
                        autoComplete="name"
                        required
                    />
                    <Field
                        label="Email"
                        type="email"
                        testId={REGISTER.emailInput}
                        value={email}
                        onChange={setEmail}
                        autoComplete="email"
                        required
                    />
                    <Field
                        label="Password"
                        type="password"
                        testId={REGISTER.passwordInput}
                        value={password}
                        onChange={setPassword}
                        autoComplete="new-password"
                        required
                    />
                    <Field
                        label="Confirm password"
                        type="password"
                        testId={REGISTER.passwordConfirmInput}
                        value={confirm}
                        onChange={setConfirm}
                        autoComplete="new-password"
                        required
                    />

                    {error && (
                        <p
                            data-testid="register-error"
                            className="text-sm text-[#FF4D4D] font-medium"
                        >
                            {error}
                        </p>
                    )}

                    <button
                        type="submit"
                        data-testid={REGISTER.submitButton}
                        disabled={submitting}
                        className="w-full h-14 rounded-2xl bg-primary text-primary-foreground font-bold tracking-tight shadow-neon hover:-translate-y-[2px] active:scale-[0.99] transition-all disabled:opacity-60 disabled:hover:translate-y-0 inline-flex items-center justify-center gap-2"
                    >
                        {submitting ? 'Creating…' : 'Create account'}
                        {!submitting && <ArrowRight size={18} weight="bold" />}
                    </button>
                </form>

                <p className="mt-6 text-sm text-muted-foreground text-center">
                    Already have an account?{' '}
                    <Link
                        to="/login"
                        data-testid={REGISTER.loginLink}
                        className="text-foreground font-semibold hover:underline underline-offset-4"
                    >
                        Sign in
                    </Link>
                </p>
            </div>
        </main>
    );
}

function Field({ label, type, value, onChange, testId, autoComplete, required }) {
    return (
        <label className="block">
            <span className="uppercase-mono text-muted-foreground block mb-2">
                {label}
            </span>
            <input
                data-testid={testId}
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                autoComplete={autoComplete}
                required={required}
                className="w-full h-12 rounded-2xl border border-foreground/15 bg-foreground/5 px-4 outline-none focus:border-foreground/40 transition-colors placeholder:text-muted-foreground/70"
            />
        </label>
    );
}
